import {
  ADMIN_AUDIT_SUMMARY_UNKNOWN,
  getAdminAuditAction,
  isKnownAdminAuditTargetType,
  resolveAdminAuditSummaryKey,
} from '../../constants/adminAudit.js';
import { ADMIN_DATE_PLACEHOLDER } from './adminUserDisplay.js';

export { ADMIN_DATE_PLACEHOLDER, resolveAdminAuditSummaryKey };

const KNOWN_ROLES = new Set(['super_admin', 'admin', 'support']);

function pickString(value) {
  if (typeof value !== 'string') return '';
  return value.trim();
}

function sanitizeRawToken(value) {
  const raw = pickString(value).slice(0, 128);
  if (!raw) return '';
  return /^[A-Za-z0-9_.:-]+$/.test(raw) ? raw : '';
}

/**
 * @param {unknown} role
 * @param {(key: string) => string} t
 * @returns {string}
 */
export function adminAuditRoleLabel(role, t) {
  if (role == null || pickString(role) === '') return t('admin.audit.role.none');
  const normalized = pickString(role);
  if (KNOWN_ROLES.has(normalized)) {
    return t(`admin.audit.role.${normalized}`);
  }
  return sanitizeRawToken(normalized) || t('admin.audit.role.unknown');
}

/**
 * @param {{ displayName?: unknown, email?: unknown, uid?: unknown, id?: unknown } | null | undefined} person
 */
export function resolveAuditIdentity(person) {
  const displayName = pickString(person?.displayName) || null;
  const email = pickString(person?.email) || null;
  const uid = pickString(person?.uid) || pickString(person?.id) || null;
  const primary = displayName || email || uid || ADMIN_DATE_PLACEHOLDER;
  return { displayName, email, uid, primary, missingAuth: Boolean(uid) && !displayName && !email };
}

/**
 * @param {{ action?: unknown, summaryKey?: unknown }} log
 * @param {(key: string) => string} t
 * @returns {{ label: string, rawAction: string | null }}
 */
export function adminAuditActionLabel(log, t) {
  const key = resolveAdminAuditSummaryKey(log);
  const label = t(key);
  const resolved = label === key ? t(ADMIN_AUDIT_SUMMARY_UNKNOWN) : label;
  const rawAction = pickString(log?.action) || null;
  const known = Boolean(getAdminAuditAction(rawAction));
  return {
    label: resolved,
    rawAction: known || !rawAction ? rawAction : sanitizeRawToken(rawAction) || rawAction,
  };
}

/**
 * @param {{ targetType?: unknown, target?: object }} log
 */
export function resolveAuditTarget(log) {
  const targetType = pickString(log?.targetType) || 'user';
  const target = log?.target && typeof log.target === 'object' ? log.target : {};
  if (targetType === 'user') {
    return {
      kind: 'user',
      targetType,
      identity: resolveAuditIdentity({
        displayName: target.displayName,
        email: target.email,
        uid: target.uid || target.id,
        id: target.id,
      }),
    };
  }
  if (isKnownAdminAuditTargetType(targetType)) {
    const id = pickString(target.id) || pickString(target.uid) || null;
    return {
      kind: 'generic',
      targetType,
      id,
      identity: resolveAuditIdentity({ uid: id, id }),
    };
  }
  return {
    kind: 'generic',
    targetType: sanitizeRawToken(targetType) || targetType,
    id: pickString(target.id) || null,
    identity: resolveAuditIdentity(target),
  };
}

/**
 * Structured detail rows from the allowlisted payload only. Never dump JSON.
 * @param {object} log
 * @param {(key: string) => string} t
 * @returns {{ key: string, label: string, value: string }[]}
 */
export function adminAuditDetailFields(log, t) {
  const action = pickString(log?.action);
  const metadata = log?.metadata && typeof log.metadata === 'object' && !Array.isArray(log.metadata)
    ? log.metadata
    : {};
  if (action === 'role_changed') {
    return [
      { key: 'from', label: t('admin.audit.details.from'), value: adminAuditRoleLabel(metadata.from, t) },
      { key: 'to', label: t('admin.audit.details.to'), value: adminAuditRoleLabel(metadata.to, t) },
    ];
  }
  if (action === 'prospect_created') {
    const source = pickString(metadata.source);
    if (!source) return [];
    return [{ key: 'source', label: t('admin.audit.details.source'), value: source }];
  }
  if (action === 'prospect_updated') {
    const fields = Array.isArray(metadata.fields)
      ? metadata.fields.filter((item) => typeof item === 'string' && item.trim()).map((item) => item.trim())
      : [];
    if (!fields.length) return [];
    return [{ key: 'fields', label: t('admin.audit.details.fields'), value: fields.join(', ') }];
  }
  if (action === 'prospect_projection_verification_updated') {
    const rows = [];
    const section = pickString(metadata.section);
    if (section) rows.push({ key: 'section', label: t('admin.audit.details.section'), value: section });
    const fromState = pickString(metadata.fromState);
    if (fromState) rows.push({ key: 'fromState', label: t('admin.audit.details.fromState'), value: fromState });
    const toState = pickString(metadata.toState);
    if (toState) rows.push({ key: 'toState', label: t('admin.audit.details.toState'), value: toState });
    const campaignId = pickString(metadata.campaignId);
    if (campaignId) rows.push({ key: 'campaignId', label: t('admin.audit.details.campaignId'), value: campaignId });
    const mailingId = pickString(metadata.mailingId);
    if (mailingId) rows.push({ key: 'mailingId', label: t('admin.audit.details.mailingId'), value: mailingId });
    const responseIntent = pickString(metadata.responseIntent);
    if (responseIntent) {
      rows.push({ key: 'responseIntent', label: t('admin.audit.details.responseIntent'), value: responseIntent });
    }
    return rows;
  }
  if (action === 'prospects_imported') {
    const rows = [];
    for (const key of ['inserted', 'skippedDuplicate', 'failed']) {
      const value = metadata[key];
      if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) continue;
      rows.push({ key, label: t(`admin.audit.details.${key}`), value: String(value) });
    }
    return rows;
  }
  if (action === 'prospect_campaign_created') {
    const rows = [];
    const campaignId = pickString(metadata.campaignId);
    if (campaignId) {
      rows.push({ key: 'campaignId', label: t('admin.audit.details.campaignId'), value: campaignId });
    }
    const selectionMode = pickString(metadata.selectionMode);
    if (selectionMode) {
      rows.push({
        key: 'selectionMode',
        label: t('admin.audit.details.selectionMode'),
        value: selectionMode,
      });
    }
    if (typeof metadata.targetCount === 'number' && Number.isInteger(metadata.targetCount) && metadata.targetCount >= 0) {
      rows.push({
        key: 'targetCount',
        label: t('admin.audit.details.targetCount'),
        value: String(metadata.targetCount),
      });
    }
    return rows;
  }
  if (action === 'prospect_public_responses_reconciled') {
    const rows = [];
    if (metadata.dryRun === true || metadata.dryRun === false) {
      rows.push({
        key: 'dryRun',
        label: t('admin.audit.details.dryRun'),
        value: String(metadata.dryRun),
      });
    }
    for (const key of ['scanned', 'projected', 'skipped', 'orphaned', 'failed']) {
      const value = metadata[key];
      if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) continue;
      rows.push({ key, label: t(`admin.audit.details.${key}`), value: String(value) });
    }
    return rows;
  }
  if (action === 'prospect_campaign_mailings_prepared') {
    const rows = [];
    const campaignId = pickString(metadata.campaignId);
    if (campaignId) {
      rows.push({ key: 'campaignId', label: t('admin.audit.details.campaignId'), value: campaignId });
    }
    if (typeof metadata.mailingCount === 'number' && Number.isInteger(metadata.mailingCount) && metadata.mailingCount >= 0) {
      rows.push({
        key: 'mailingCount',
        label: t('admin.audit.details.mailingCount'),
        value: String(metadata.mailingCount),
      });
    }
    if (typeof metadata.targetCount === 'number' && Number.isInteger(metadata.targetCount) && metadata.targetCount >= 0) {
      rows.push({
        key: 'targetCount',
        label: t('admin.audit.details.targetCount'),
        value: String(metadata.targetCount),
      });
    }
    if (typeof metadata.mailingVersion === 'number' && Number.isInteger(metadata.mailingVersion) && metadata.mailingVersion >= 0) {
      rows.push({
        key: 'mailingVersion',
        label: t('admin.audit.details.mailingVersion'),
        value: String(metadata.mailingVersion),
      });
    }
    return rows;
  }
  if (
    action === 'prospect_campaign_letter_archive_requested' ||
    action === 'prospect_campaign_letter_archive_ready' ||
    action === 'prospect_campaign_letter_archive_downloaded'
  ) {
    const rows = [];
    const campaignId = pickString(metadata.campaignId);
    if (campaignId) {
      rows.push({ key: 'campaignId', label: t('admin.audit.details.campaignId'), value: campaignId });
    }
    if (
      typeof metadata.letterCount === 'number' &&
      Number.isInteger(metadata.letterCount) &&
      metadata.letterCount >= 0
    ) {
      rows.push({
        key: 'letterCount',
        label: t('admin.audit.details.letterCount'),
        value: String(metadata.letterCount),
      });
    }
    if (
      typeof metadata.byteSize === 'number' &&
      Number.isInteger(metadata.byteSize) &&
      metadata.byteSize >= 0
    ) {
      rows.push({ key: 'byteSize', label: t('admin.audit.details.byteSize'), value: String(metadata.byteSize) });
    }
    return rows;
  }
  if (action === 'prospect_campaign_letter_archive_failed') {
    const rows = [];
    const campaignId = pickString(metadata.campaignId);
    if (campaignId) {
      rows.push({ key: 'campaignId', label: t('admin.audit.details.campaignId'), value: campaignId });
    }
    const failureCode = pickString(metadata.failureCode);
    if (failureCode) {
      rows.push({ key: 'failureCode', label: t('admin.audit.details.failureCode'), value: failureCode });
    }
    return rows;
  }
  if (
    action === 'prospect_campaign_build_recovered' ||
    action === 'prospect_campaign_build_recovery_enqueue_failed' ||
    action === 'prospect_campaign_build_retry_exhausted' ||
    action === 'prospect_campaign_mailing_build_recovered' ||
    action === 'prospect_campaign_mailing_build_recovery_enqueue_failed' ||
    action === 'prospect_campaign_mailing_build_retry_exhausted' ||
    action === 'prospect_campaign_letter_archive_recovered'
  ) {
    const rows = [];
    const campaignId = pickString(metadata.campaignId);
    if (campaignId) {
      rows.push({ key: 'campaignId', label: t('admin.audit.details.campaignId'), value: campaignId });
    }
    const source = pickString(metadata.source);
    if (source) {
      rows.push({ key: 'source', label: t('admin.audit.details.source'), value: source });
    }
    if (
      typeof metadata.recoveryAttemptCount === 'number' &&
      Number.isInteger(metadata.recoveryAttemptCount) &&
      metadata.recoveryAttemptCount >= 0
    ) {
      rows.push({
        key: 'recoveryAttemptCount',
        label: t('admin.audit.details.recoveryAttemptCount'),
        value: String(metadata.recoveryAttemptCount),
      });
    }
    const failureCode = pickString(metadata.failureCode);
    if (failureCode) {
      rows.push({ key: 'failureCode', label: t('admin.audit.details.failureCode'), value: failureCode });
    }
    const reason = pickString(metadata.reason);
    if (reason) {
      rows.push({ key: 'reason', label: t('admin.audit.details.reason'), value: reason });
    }
    return rows;
  }
  if (
    action === 'prospect_letter_template_created' ||
    action === 'prospect_letter_template_version_created' ||
    action === 'prospect_letter_template_duplicated' ||
    action === 'prospect_letter_template_archived'
  ) {
    const rows = [];
    const templateId = pickString(metadata.templateId);
    if (templateId) {
      rows.push({ key: 'templateId', label: t('admin.audit.details.templateId'), value: templateId });
    }
    if (typeof metadata.version === 'number' && Number.isInteger(metadata.version)) {
      rows.push({ key: 'version', label: t('admin.audit.details.version'), value: String(metadata.version) });
    }
    const sourceTemplateId = pickString(metadata.sourceTemplateId);
    if (sourceTemplateId) {
      rows.push({
        key: 'sourceTemplateId',
        label: t('admin.audit.details.sourceTemplateId'),
        value: sourceTemplateId,
      });
    }
    if (typeof metadata.bilingual === 'boolean') {
      rows.push({
        key: 'bilingual',
        label: t('admin.audit.details.bilingual'),
        value: metadata.bilingual ? t('admin.users.yes') : t('admin.users.no'),
      });
    }
    for (const [key, labelKey] of [
      ['placeholderCount', 'placeholderCount'],
      ['qrBlockCount', 'qrBlockCount'],
      ['languageCount', 'languageCount'],
      ['imageCount', 'imageCount'],
      ['fontFamilyCount', 'fontFamilyCount'],
    ]) {
      const value = metadata[key];
      if (typeof value === 'number' && Number.isInteger(value) && value >= 0) {
        rows.push({ key, label: t(`admin.audit.details.${labelKey}`), value: String(value) });
      }
    }
    return rows;
  }
  if (action === 'prospect_letter_template_asset_uploaded') {
    const rows = [];
    const assetId = pickString(metadata.assetId);
    if (assetId) {
      rows.push({ key: 'assetId', label: t('admin.audit.details.assetId'), value: assetId });
    }
    const contentType = pickString(metadata.contentType);
    if (contentType) {
      rows.push({ key: 'contentType', label: t('admin.audit.details.contentType'), value: contentType });
    }
    for (const [key, labelKey] of [
      ['byteSize', 'byteSize'],
      ['width', 'width'],
      ['height', 'height'],
    ]) {
      const value = metadata[key];
      if (typeof value === 'number' && Number.isInteger(value) && value >= 0) {
        rows.push({ key, label: t(`admin.audit.details.${labelKey}`), value: String(value) });
      }
    }
    return rows;
  }
  return [];
}

/**
 * Fail-closed: details UI must never surface these fragments.
 * @param {unknown} payload
 * @returns {string[]}
 */
export function findForbiddenAuditUiFragments(payload) {
  const json = JSON.stringify(payload);
  const hits = [];
  const forbidden = [
    'customClaims',
    'customToken',
    'inviteToken',
    'invitationLink',
    'operationId',
    'targetEmail',
  ];
  for (const fragment of forbidden) {
    if (json.includes(fragment)) hits.push(fragment);
  }
  return hits;
}
